import type { ManifestObjectAppearance } from "daedalus-core";
import { SIXTEENTHS } from "../movement/step.ts";
import { WORLD_TILE_PIXELS, widestLoadedView, type Size } from "./vision.ts";

/**
 * The window a thing is shown in (ADR-034's amendment of 2026-10-06). A thing placed in the world (a shrine, a chest, a
 * sign, a gate) is world art with a state, as a prop is, so a page must draw it wherever a prop standing there would be
 * drawn: while any pixel of its art is inside the view. The map server decides what a page holds, so it judges a thing
 * on the box its art fills, not on its feet point, against the widest view any screen can show, laid where the page's
 * bounded camera lays its view. A character is a different matter and keeps the vision rectangle around the viewer.
 *
 * Everything here is arithmetic on sixteenths of a tile and allocates nothing but `thingReach`'s result, which is
 * worked out once a thing when a world is mounted.
 */

/**
 * The view a thing is judged against, in tiles: the widest any screen shows at any zoom a player can choose
 * (`widestLoadedView`, 54 by 30), which is also what the page's chunk painter loads props for. The knob of how far a
 * thing is sent: it follows the zoom rules, and an opaque fog past the vision cap would let it fall to the cap.
 */
export const THING_VIEW_TILES: Size = Object.freeze({ width: widestLoadedView().width / WORLD_TILE_PIXELS, height: widestLoadedView().height / WORLD_TILE_PIXELS });

/**
 * How far above its feet a state reaches when the version does not say how tall its frame is, in tiles. A compiled
 * appearance carries each state's frame width and, with its footprint, its frame height; a state with no footprint has
 * no height on the map server, so it is taken as this tall. Too tall costs a thing staying in a view a little longer;
 * too short would cut a taller thing's top off the view.
 */
export const THING_UNSIZED_HEIGHT_TILES = 8;

/**
 * The most a reach holds, so it fits an unsigned sixteen-bit table.
 */
const MOST_REACH = 0xffff;

/**
 * How far a thing's art reaches from its feet point, in sixteenths of a tile: to each side, above and below.
 */
export interface ThingReach {
  readonly across: number;
  readonly up: number;
  readonly down: number;
}

/**
 * A length in pixels as sixteenths of a tile, rounded out and held to sixteen bits.
 *
 * @param pixels The length in world pixels.
 * @param tileSize Tile size in pixels.
 * @returns The sixteenths.
 */
function sixteenthsOf(pixels: number, tileSize: number): number {
  return Math.min(MOST_REACH, Math.ceil((pixels * SIXTEENTHS) / tileSize));
}

/**
 * How far a thing's art reaches from its feet point, over every state it can rest on. A state's frame is drawn
 * centred on the feet point and standing on it, moved by the appearance's pixel offset (`drawnFrameAnchor`), so it
 * reaches half its width to each side and its height above. The widest and the tallest state are taken, since the
 * state changes while the thing is in view; a transition's strip is taken to fit inside them, as the strips cut beside
 * a state's frame do. The offset widens every side it could move the frame toward.
 *
 * @param look The thing's appearance.
 * @param tileSize The world's tile size in pixels.
 * @returns The reach, at least one sixteenth across so a thing is never mistaken for a point.
 * @example
 * thingReach(obelisk, 32);
 * // { across: 74, up: 156, down: 0 } for a 295 by 311 frame
 */
export function thingReach(look: ManifestObjectAppearance, tileSize: number): ThingReach {
  let width = 0;
  let height = 0;
  for (const state of look.states) {
    width = Math.max(width, state.width > 0 ? state.width : (state.footprint?.w ?? tileSize));
    height = Math.max(height, state.footprint?.h ?? THING_UNSIZED_HEIGHT_TILES * tileSize);
  }
  const offsetX = Math.abs(look.offset?.x ?? 0);
  const offsetY = Math.abs(look.offset?.y ?? 0);
  return { across: Math.max(1, sixteenthsOf(width / 2 + offsetX, tileSize)), up: sixteenthsOf(height + offsetY, tileSize), down: sixteenthsOf(offsetY, tileSize) };
}

/**
 * Where a window starts along one axis: centred on the viewer, and stopped at the map's edges as the page's bounded
 * camera is, so a viewer near an edge has the window the page shows there and not one half off the map. A map smaller
 * than the window starts it at the map's start.
 *
 * @param centre The viewer's coordinate.
 * @param half Half the window's length.
 * @param extent The map's length, in the same units.
 * @returns The window's start; its end is `2 * half` on.
 */
export function windowStart(centre: number, half: number, extent: number): number {
  return Math.max(0, Math.min(centre - half, extent - 2 * half));
}

/**
 * Whether any of a thing's art is inside a viewer's window, edges included.
 *
 * @param viewerX The viewer's feet across, in sixteenths.
 * @param viewerY The viewer's feet down.
 * @param x The thing's feet across.
 * @param y The thing's feet down.
 * @param across How far its art reaches to each side.
 * @param up How far above its feet.
 * @param down How far below.
 * @param halfWidth Half the window's width, in sixteenths.
 * @param halfHeight Half its height.
 * @param mapWidth The map's width, in sixteenths.
 * @param mapHeight The map's height.
 * @returns True when the art's box and the window overlap.
 */
export function artInWindow(viewerX: number, viewerY: number, x: number, y: number, across: number, up: number, down: number, halfWidth: number, halfHeight: number, mapWidth: number, mapHeight: number): boolean {
  const left = windowStart(viewerX, halfWidth, mapWidth);
  const top = windowStart(viewerY, halfHeight, mapHeight);
  return x + across >= left && x - across <= left + 2 * halfWidth && y + down >= top && y - up <= top + 2 * halfHeight;
}
